import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import Button from '../../ui/buttons/Button';
import Input from '../../ui/inputs/Input';
import CnpjInput from '../../ui/inputs/CnpjInput';
import CustomCheckbox from '../../ui/checkboxes/CustomCheckbox';
import Select from '../../ui/selects/Select';
import { BanknoteArrowUp, Car, Pen, X } from 'lucide-react';
import { useToast } from '../../ui/toast/ToastContext';
import SecondaryButton from '../../ui/buttons/SecondaryButton';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { fetchWithCsrf } from '../../../utils/fetchWithCsrf';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { cnpj } from 'cpf-cnpj-validator';

const AddAssetModal = ({ assetId, onClose, onSuccess, initialAssetType }) => {
  const [form, setForm] = useState({
    alias: '', //
    strategy: '', //
    assetType: initialAssetType || 'investment_funds', //
    category: '', //
    kind: '', //
    status: 'active', //
    cnpj: '', //
    ticker: '', //
    sector: '',
    locked: false, //
    apiLocked: false, //
  });

  const { addToast } = useToast();

  // Fetch inicial do ativo
  // useEffect(() => {
  //   fetch(`/app/analyst/assets/${assetId}`)
  //     .then((res) => res.json())
  //     .then((data) => {
  //       setForm(camelizeKeys(data));
  //     })
  //     .catch((err) => {
  //       console.error('Erro ao carregar ativo', err);
  //       addToast({ type: 'error', message: 'Erro ao carregar dados do ativo' });
  //     });
  // }, [assetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (name) => {
    setForm((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = snakecaseKeys({ asset: form });

    try {
      const res = await fetchWithCsrf(`/app/analyst/assets`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        const json = await res.json();
        throw new Error(json.message || 'Erro ao adicionar ativo');
      }

      onClose();
      addToast({ type: 'success', message: 'Ativo adicionado com sucesso!' });
      onSuccess();
    } catch (err) {
      addToast({ type: 'error', message: err.message });
    }
  };

  const strategyOptions = {
    investment_funds: [
      { label: '-', value: '-' },
      { label: 'Pós', value: 'Pós' },
      { label: 'Pré', value: 'Pré' },
      { label: 'IPCA+', value: 'IPCA+' },
      { label: 'Brasil', value: 'Brasil' },
      { label: 'Internacional', value: 'Internacional' },
    ],
    pension_funds: [
      { label: 'Small Caps', value: 'Small Caps' },
      { label: 'Crédito Estruturado', value: 'Crédito Estruturado' },
      { label: 'Crédito Pós', value: 'Crédito Pós' },
      { label: 'Long Only', value: 'Long Only' },
      { label: 'Inflação', value: 'Inflação' },
      { label: 'Ativo', value: 'Ativo' },
      { label: 'Long Short', value: 'Long Short' },
      { label: 'Pós-fixado', value: 'Pós-fixado' },
      { label: 'Pré-fixado', value: 'Pré-fixado' },
      { label: 'Crédito High Yield', value: 'Crédito High Yield' },
      { label: 'Long Biased', value: 'Long Biased' },
      { label: 'Investimento no Exterior', value: 'Investimento no Exterior' },
      { label: 'Macro', value: 'Macro' },
      { label: 'Criptoativo', value: 'Criptoativo' },
      { label: 'Temáticos', value: 'Temáticos' },
      { label: 'Imobiliário', value: 'Imobiliário' },
      { label: '-', value: '-' },
    ],
    equities: [
      { label: 'Brasil', value: 'Brasil' },
      { label: 'Internacional', value: 'Internacional' },
    ],
  };

  return (
    <Modal onClose={onClose}>
      <div className="p-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-blueDarker font-jost text-2xl font-semibold">
            <BanknoteArrowUp size={24} className="text-tealMedium" strokeWidth={1.25} />
            Adicionar Ativo
          </div>
          <button onClick={onClose}>
            <X size={24} className="text-tealBase hover:opacity-80" strokeWidth={1.25} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 font-jost text-sm">
          <div className="col-span-1 md:col-span-2">
            <Input name="alias" label="Nome de exibição" value={form.alias} onChange={handleChange} required />
          </div>
          <div className="col-span-1 md:col-span-2">
            <Select
              name="assetType"
              label="Tipo do Ativo"
              value={form.assetType}
              onChange={handleChange}
              options={[
                { label: 'Fundo de Investimento', value: 'investment_funds' },
                { label: 'Fundo de Previdência', value: 'pension_funds' },
                { label: 'Renda Variável', value: 'equities' },
              ]}
              required
            />
          </div>
          {form.assetType === 'equities' ? (
            <Select
              name="kind"
              label="Tipo (BTG)"
              value={form.kind}
              onChange={handleChange}
              options={[
                { label: 'Ação', value: 'stock' },
                { label: 'FII', value: 'fii' },
              ]}
              required
            />
          ) : (
            <Select
              name="category"
              label="Categoria"
              value={form.category}
              onChange={handleChange}
              options={[
                { label: 'Renda Fixa', value: 'Renda Fixa' },
                { label: 'Multimercado', value: 'Multimercado' },
                { label: 'Ações', value: 'Ações' },
                { label: 'Alternativos', value: 'Alternativos' },
              ]}
              required
            />
          )}
          <Select
            name="strategy"
            label="Estratégia"
            value={form.strategy}
            onChange={handleChange}
            options={strategyOptions[form.assetType]}
            required
          />
          {form.assetType === 'equities' ? (
            <Input name="ticker" label="Ticker" value={form.ticker} onChange={handleChange} required />
          ) : (
            <CnpjInput name="cnpj" label="CNPJ" value={form.cnpj} onChange={handleChange} required />
          )}
          <Select
            name="status"
            label="Status"
            value={form.status}
            onChange={handleChange}
            options={[
              { label: 'Ativo', value: 'active' },
              { label: 'Fechado p/ captação', value: 'closed' },
              { label: 'Em liquidação', value: 'sale' },
            ]}
            required
          />
          {form.assetType === 'equities' && (
            <div className="col-span-1 md:col-span-2">
              <Select
                name="sector"
                label="Setor"
                value={form.sector}
                onChange={handleChange}
                options={[
                  { label: 'Comércio Varejista', value: 'Retail Trade' },
                  { label: 'Mineração de Energia', value: 'Energy Minerals' },
                  { label: 'Serviços de Saúde', value: 'Health Services' },
                  { label: 'Utilidades', value: 'Utilities' },
                  { label: 'Finanças', value: 'Finance' },
                  { label: 'Serviços ao Consumidor', value: 'Consumer Services' },
                  { label: 'Bens de Consumo Não Duráveis', value: 'Consumer Non-Durables' },
                  { label: 'Mineração Não Energética', value: 'Non-Energy Minerals' },
                  { label: 'Serviços Comerciais', value: 'Commercial Services' },
                  { label: 'Serviços de Distribuição', value: 'Distribution Services' },
                  { label: 'Transporte', value: 'Transportation' },
                  { label: 'Serviços de Tecnologia', value: 'Technology Services' },
                  { label: 'Indústrias de Processo', value: 'Process Industries' },
                  { label: 'Comunicações', value: 'Communications' },
                  { label: 'Fabricação de Produtores', value: 'Producer Manufacturing' },
                  { label: 'Diversos', value: 'Miscellaneous' },
                  { label: 'Tecnologia Eletrônica', value: 'Electronic Technology' },
                  { label: 'Serviços Industriais', value: 'Industrial Services' },
                  { label: 'Tecnologia de Saúde', value: 'Health Technology' },
                  { label: 'Bens de Consumo Duráveis', value: 'Consumer Durables' },
                ]}
                required
              />
            </div>
          )}
          <CustomCheckbox
            labelClassName="text-base"
            name="apiLocked"
            label="Travar atualização via API / Importação"
            checked={form.apiLocked}
            onChange={() => handleCheckbox('apiLocked')}
          />

          <div className="flex justify-end items-center mt-1">
            <div className="flex justify-between gap-2">
              <SecondaryButton onClick={onClose}>CANCELAR</SecondaryButton>
              <Button type="submit">SALVAR</Button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default AddAssetModal;
